/**
 * The inspector: six tabs beside the map, the active one in the URL.
 *
 * A real `role="tablist"` with arrow-key navigation, because this is the app's
 * main switch and a pilot with a keyboard should not have to tab through six
 * buttons to reach the sixth. The panes come from `tabs.tsx`; this file knows
 * how a tab looks and nothing about what is inside one.
 */
import { useRef } from 'react';
import { useT } from '../i18n';
import { IconChevronLeft, IconChevronRight } from './icons';
import { TABS, type PaneContext, type TabDef, type TabKey } from './tabs';

export function Inspector({
  tab,
  onTab,
  ctx,
  collapsed = false,
  onToggleCollapse,
  tabs = TABS,
}: {
  tab: TabKey;
  onTab(next: TabKey): void;
  ctx: PaneContext;
  collapsed?: boolean;
  onToggleCollapse?: (() => void) | undefined;
  /** the shared read-only view drops Export — there is nothing there to do */
  tabs?: readonly TabDef[];
}) {
  const t = useT();
  const listRef = useRef<HTMLDivElement>(null);

  if (collapsed) {
    return (
      <button
        type="button"
        className="ae-inspector-collapse"
        aria-label={t('insp.expand')}
        title={t('insp.expand')}
        onClick={onToggleCollapse}
      >
        <IconChevronLeft />
      </button>
    );
  }

  const onKey = (e: React.KeyboardEvent) => {
    const i = tabs.findIndex(x => x.key === tab);
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
      if (next) {
        onTab(next.key);
        listRef.current?.querySelector<HTMLButtonElement>(`[data-tab="${next.key}"]`)?.focus();
      }
    }
  };

  const active = tabs.find(x => x.key === tab) ?? tabs[0];

  return (
    <div className="ae-inspector">
      <div className="ae-tabs" role="tablist" aria-label={t('insp.tabs')} ref={listRef} onKeyDown={onKey}>
        {tabs.map(x => (
          <button
            key={x.key}
            type="button"
            role="tab"
            data-tab={x.key}
            className="ae-tab"
            aria-selected={x.key === tab}
            tabIndex={x.key === tab ? 0 : -1}
            onClick={() => onTab(x.key)}
          >
            {t(x.labelKey)}
          </button>
        ))}
        {onToggleCollapse ? (
          <button
            type="button"
            className="ae-btn ae-btn-icon ae-tabs-collapse"
            aria-label={t('insp.collapse')}
            title={t('insp.collapse')}
            onClick={onToggleCollapse}
          >
            <IconChevronRight />
          </button>
        ) : null}
      </div>
      <div className={'ae-pane' + (active?.bare ? ' bare' : '')} role="tabpanel" aria-label={t(active?.labelKey ?? '')}>
        {active?.render(ctx)}
      </div>
    </div>
  );
}
